import * as PIXI from 'pixi.js';
import { useTokenStore } from '../stores/tokenStore';
import { LIGHT_PRESETS } from '@machina/core';

/**
 * IsoLightingSystem
 *
 * HD-2D lighting overlay using a PIXI.Sprite with a dynamically generated
 * canvas texture. Simpler than shaders, guaranteed to work.
 *
 * Draws a dark overlay with radial gradient "holes" punched at each token.
 * Tokens with `emitsLight` use the preset's radius and color.
 * Updated every frame via ticker.
 */

/** Pixels per 5-foot square in the isometric view */
const PIXELS_PER_FOOT = 50 / 5; // 10px per foot in top-down, iso scaling handled below

export class IsoLightingSystem {
  public container: PIXI.Container;

  private overlay: PIXI.Sprite;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private texture: PIXI.Texture;
  private ticker: PIXI.Ticker | null = null;
  private tickerFn: (() => void) | null = null;

  private canvasWidth: number;
  private canvasHeight: number;
  // Readable system-level default (overridable via setAmbient). A light warm
  // haze rather than a near-black overlay, so scenes are visible without torches.
  private ambientColor = { r: 24, g: 21, b: 16 };
  private ambientAlpha = 0.22;
  private lightRadius = 180;
  private flickerPhase = 0;

  constructor(width: number, height: number) {
    this.container = new PIXI.Container();
    this.canvasWidth = width;
    this.canvasHeight = height;

    // Create an offscreen canvas for the lighting overlay
    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx = this.canvas.getContext('2d')!;

    this.texture = PIXI.Texture.from(this.canvas);
    this.overlay = new PIXI.Sprite(this.texture);
    this.overlay.blendMode = PIXI.BLEND_MODES.MULTIPLY;
    this.container.addChild(this.overlay);
  }

  setTicker(ticker: PIXI.Ticker): void {
    this.ticker = ticker;
    this.tickerFn = () => {
      this.flickerPhase += 0.016;
      this.render();
    };
    ticker.add(this.tickerFn);
  }

  setAmbient(color: { r: number; g: number; b: number }, alpha: number): void {
    this.ambientColor = color;
    this.ambientAlpha = alpha;
  }

  setLightRadius(radius: number): void {
    this.lightRadius = radius;
  }

  resize(width: number, height: number): void {
    this.canvasWidth = width;
    this.canvasHeight = height;
    this.canvas.width = width;
    this.canvas.height = height;
    this.texture.update();
  }

  /** Get world container from the stage (sibling of this container) */
  private getWorldContainer(): PIXI.Container | undefined {
    return this.container.parent?.children.find(
      (c) => c !== this.container && c instanceof PIXI.Container && c.sortableChildren
    ) as PIXI.Container | undefined;
  }

  /** Convert top-down grid position to screen coords via the world container */
  private toScreen(x: number, y: number, worldContainer?: PIXI.Container): { x: number; y: number } {
    const isoX = (x / 50 - y / 50) * 32;
    const isoY = (x / 50 + y / 50) * 16;

    if (worldContainer) {
      const global = worldContainer.toGlobal(new PIXI.Point(isoX, isoY));
      return { x: global.x, y: global.y };
    }
    return { x: isoX, y: isoY };
  }

  private render(): void {
    const ctx = this.ctx;
    const w = this.canvasWidth;
    const h = this.canvasHeight;

    // Fill with dark ambient overlay
    const { r, g, b } = this.ambientColor;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${this.ambientAlpha})`;
    ctx.fillRect(0, 0, w, h);

    // Punch light holes using 'destination-out' — erases the darkness
    ctx.globalCompositeOperation = 'destination-out';

    const tokens = useTokenStore.getState().tokens;
    const worldContainer = this.getWorldContainer();

    for (const token of tokens.values()) {
      if (token.hpTier === 'dead') continue;

      const screen = this.toScreen(token.position.x, token.position.y, worldContainer);

      // Determine light radius and behavior
      let radius: number;
      let flickerAmount: number;
      let hasWarmTint = false;

      if (token.emitsLight) {
        const preset = LIGHT_PRESETS[token.emitsLight];
        if (preset) {
          // Convert feet to approximate screen pixels
          // In iso view, world container is scaled, so use the scaled radius
          const totalFeet = preset.bright + preset.dim;
          const worldScale = worldContainer?.scale.x ?? 1;
          radius = totalFeet * PIXELS_PER_FOOT * worldScale * 0.7; // 0.7 = iso foreshortening
          flickerAmount = preset.type === 'fire' ? 0.05 : 0.015;
          hasWarmTint = preset.type === 'fire';
        } else {
          radius = this.lightRadius;
          flickerAmount = 0.04;
        }
      } else {
        // Default character ambient light
        radius = this.lightRadius;
        flickerAmount = 0.04;
      }

      // Flicker
      const flicker = 1 + flickerAmount * Math.sin(this.flickerPhase * 2.2 + screen.x * 0.01)
                        + (flickerAmount * 0.5) * Math.sin(this.flickerPhase * 3.7 + screen.y * 0.01);
      const finalRadius = radius * flicker;

      // Oblong light — taller than wide to cover full sprite height
      const scaleX = 1.0;
      const scaleY = 1.4;

      ctx.save();
      ctx.translate(screen.x, screen.y - finalRadius * 0.2);
      ctx.scale(scaleX, scaleY);

      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, finalRadius);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
      gradient.addColorStop(0.4, 'rgba(0, 0, 0, 0.8)');
      gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.3)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(0, 0, finalRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // For fire sources, paint a warm color tint OVER the cleared area
      if (hasWarmTint) {
        ctx.globalCompositeOperation = 'source-over';
        ctx.save();
        ctx.translate(screen.x, screen.y - finalRadius * 0.2);
        ctx.scale(scaleX, scaleY);

        const warmGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, finalRadius);
        warmGrad.addColorStop(0, 'rgba(255, 140, 40, 0.06)');
        warmGrad.addColorStop(0.5, 'rgba(255, 100, 20, 0.03)');
        warmGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');

        ctx.fillStyle = warmGrad;
        ctx.beginPath();
        ctx.arc(0, 0, finalRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        ctx.globalCompositeOperation = 'destination-out';
      }
    }

    // Reset composite
    ctx.globalCompositeOperation = 'source-over';

    // Update the Pixi texture from the canvas
    this.texture.update();
  }

  destroy(): void {
    if (this.ticker && this.tickerFn) {
      this.ticker.remove(this.tickerFn);
    }
    this.overlay.destroy();
    this.container.destroy({ children: true });
  }
}
